import { lazy, Suspense, useEffect, useRef } from 'react'
import type { Config, Data, Layout, PlotMouseEvent } from 'plotly.js'

function isPlotMouseEvent(value: unknown): value is Readonly<PlotMouseEvent> {
  if (typeof value !== 'object' || value === null) return false
  return 'points' in value && Array.isArray((value as PlotMouseEvent).points)
}

/** PlotlyChart component that uses plotly.js-dist-min (full bundle: all trace types including 3D, WebGL, geo, etc.) */
export type PlotlyGraphDiv = HTMLDivElement & {
  data?: Data[]
  on?: (event: string, handler: (...args: unknown[]) => void) => void
  removeAllListeners?: (event: string) => void
}

function PlotlyChart({
  data,
  layout,
  config,
  onPlotlyClick,
  onPlotlyRestyle,
}: {
  data: Data[]
  layout: Partial<Layout>
  config?: Partial<Config>
  onPlotlyClick?: (event: Readonly<PlotMouseEvent>) => void
  /** Fires after Plotly restyle (e.g. legend visibility toggles). Receives the live graph div. */
  onPlotlyRestyle?: (graphDiv: PlotlyGraphDiv) => void
}) {
  const containerRef = useRef<HTMLDivElement>(null)
  const plotlyRef = useRef<typeof import('plotly.js-dist-min') | null>(null)
  const isInitializedRef = useRef(false)
  const cancelIdleRef = useRef<(() => void) | null>(null)
  const lastHeightRef = useRef<number | null>(null)
  const onPlotlyClickRef = useRef(onPlotlyClick)
  onPlotlyClickRef.current = onPlotlyClick
  const onPlotlyRestyleRef = useRef(onPlotlyRestyle)
  onPlotlyRestyleRef.current = onPlotlyRestyle

  const bindPlotlyInteractionListeners = (container: PlotlyGraphDiv) => {
    container.removeAllListeners?.('plotly_click')
    container.removeAllListeners?.('plotly_restyle')
    if (onPlotlyClickRef.current) {
      container.on?.('plotly_click', (...args: unknown[]) => {
        const e = args[0]
        if (isPlotMouseEvent(e)) onPlotlyClickRef.current?.(e)
      })
    }
    if (onPlotlyRestyleRef.current) {
      container.on?.('plotly_restyle', () => {
        onPlotlyRestyleRef.current?.(container)
      })
    }
  }

  useEffect(() => {
    let isMounted = true
    let resizeObserver: ResizeObserver | null = null
    let resizeRaf: number | null = null
    const container = containerRef.current
    if (!container) return () => {}

    const scheduleResize = () => {
      if (resizeRaf !== null || !isMounted) return
      resizeRaf = requestAnimationFrame(() => {
        resizeRaf = null
        const gd = containerRef.current
        const Plotly = plotlyRef.current
        if (!isMounted || !gd || !Plotly) return
        Plotly.relayout(gd, { autosize: true })
        Plotly.Plots.resize(gd)
      })
    }

    const init = async () => {
      if (!containerRef.current || isInitializedRef.current) return
      try {
        const Plotly = await import('plotly.js-dist-min')
        plotlyRef.current = Plotly
        if (!isMounted || !containerRef.current) return
        if (typeof window !== 'undefined') {
          ;(window as { Plotly?: unknown }).Plotly = Plotly as unknown
        }
        await Plotly.newPlot(containerRef.current, data, layout, config ?? {})
        if (!isMounted || !containerRef.current) return
        isInitializedRef.current = true
        bindPlotlyInteractionListeners(containerRef.current as PlotlyGraphDiv)
        resizeObserver = new ResizeObserver(scheduleResize)
        resizeObserver.observe(containerRef.current)
        // Plotly may compute an initial size before the container has finalized layout.
        // Force a resize on the next frame so the first paint matches the container, without
        // requiring a user interaction (drag/hover) to trigger Plotly autosize.
        scheduleResize()
      } catch (err) {
        console.error('Chart initialization failed', err)
      }
    }

    const rafId = requestAnimationFrame(() => void init())
    cancelIdleRef.current = () => cancelAnimationFrame(rafId)

    return () => {
      isMounted = false
      if (resizeRaf !== null) cancelAnimationFrame(resizeRaf)
      cancelIdleRef.current?.()
      cancelIdleRef.current = null
      resizeObserver?.disconnect()
      if (containerRef.current && plotlyRef.current) {
        try {
          plotlyRef.current.purge(containerRef.current)
        } catch {
          /* ignore */
        }
        isInitializedRef.current = false
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  useEffect(() => {
    if (!isInitializedRef.current || !plotlyRef.current || !containerRef.current) return
    plotlyRef.current
      .react(containerRef.current, data, layout, config ?? {})
      .then(() => {
        const container = containerRef.current as PlotlyGraphDiv | null
        const Plotly = plotlyRef.current
        if (!container || !Plotly) return

        bindPlotlyInteractionListeners(container)

        // Keep autosize in sync on reactive updates (layout/title changes).
        Plotly.relayout(container, { autosize: true })
        Plotly.Plots.resize(container)
      })
      .catch((err) => console.error('Plotly chart update failed', err))
  }, [data, layout, config])

  const layoutHeight =
    typeof layout?.height === 'number' && layout.height > 0 ? layout.height : undefined
  if (layoutHeight != null) lastHeightRef.current = layoutHeight
  const containerHeight = layoutHeight ?? lastHeightRef.current ?? undefined

  return (
    <div
      ref={containerRef}
      className='min-w-0 max-w-full overflow-hidden'
      style={{
        width: '100%',
        height: containerHeight != null ? containerHeight : '100%',
        minHeight: containerHeight != null ? containerHeight : undefined,
      }}
    />
  )
}

const LazyPlotlyChart = lazy(() => Promise.resolve({ default: PlotlyChart }))

export function ChartRenderer({
  data,
  layout,
  config,
  onPlotlyClick,
  onPlotlyRestyle,
}: {
  data: Data[]
  layout: Partial<Layout>
  config?: Partial<Config>
  onPlotlyClick?: (event: Readonly<PlotMouseEvent>) => void
  onPlotlyRestyle?: (graphDiv: PlotlyGraphDiv) => void
}) {
  const layoutHeight = layout?.height
  const placeholderHeight =
    typeof layoutHeight === 'number' && layoutHeight > 0 ? `${layoutHeight}px` : '100%'

  return (
    <Suspense
      fallback={
        <div
          style={{
            width: '100%',
            height: placeholderHeight,
            minHeight: placeholderHeight === '100%' ? undefined : placeholderHeight,
          }}
        />
      }
    >
      <LazyPlotlyChart
        data={data}
        layout={layout}
        config={config}
        onPlotlyClick={onPlotlyClick}
        onPlotlyRestyle={onPlotlyRestyle}
      />
    </Suspense>
  )
}
